import { useState, useEffect } from 'react'
import { api } from '../utils/api'
import { AuditLog } from '../types'

export default function AuditLogViewer() {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState({
    action: 'all',
    severity: 'all',
    resourceType: 'all',
  })
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchLogs()
  }, [page, filter])

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: '50',
      })
      if (filter.action !== 'all') params.append('action', filter.action)
      if (filter.severity !== 'all') params.append('severity', filter.severity)
      if (filter.resourceType !== 'all') params.append('resource_type', filter.resourceType)

      const res = await api.get(`/api/admin/audit/logs?${params}`)
      setLogs(res.data.items || res.data)
      setTotalPages(res.data.total_pages || 1)
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }

  const severityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'text-status-critical bg-status-critical/10'
      case 'WARNING':
        return 'text-status-warning bg-status-warning/10'
      default:
        return 'text-status-normal bg-status-normal/10'
    }
  }

// AuditLog action styles

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-muted">Loading...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <div className="flex gap-2">
          <select
            value={filter.action}
            onChange={(e) => setFilter({ ...filter, action: e.target.value })}
            className="px-3 py-2 bg-bg-panel border border-border rounded-sm text-sm text-text-primary focus:border-accent outline-none"
          >
            <option value="all">All Actions</option>
            <option value="CREATE">Create</option>
            <option value="UPDATE">Update</option>
            <option value="DELETE">Delete</option>
            <option value="IMPORT">Import</option>
            <option value="EXPORT">Export</option>
          </select>
          <select
            value={filter.severity}
            onChange={(e) => setFilter({ ...filter, severity: e.target.value })}
            className="px-3 py-2 bg-bg-panel border border-border rounded-sm text-sm text-text-primary focus:border-accent outline-none"
          >
            <option value="all">All Severity</option>
            <option value="INFO">Info</option>
            <option value="WARNING">Warning</option>
            <option value="CRITICAL">Critical</option>
          </select>
          <select
            value={filter.resourceType}
            onChange={(e) => setFilter({ ...filter, resourceType: e.target.value })}
            className="px-3 py-2 bg-bg-panel border border-border rounded-sm text-sm text-text-primary focus:border-accent outline-none"
          >
            <option value="all">All Types</option>
            <option value="RACK">Rack</option>
            <option value="DEVICE">Device</option>
            <option value="PORT">Port</option>
            <option value="CABLE">Cable</option>
          </select>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-text-muted text-lg">No audit logs found</p>
        </div>
      ) : (
        <div className="bg-bg-panel border border-border rounded-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                  Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                  Action
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                  Resource
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                  Target (DC)
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                  Severity
                </th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-b border-border last:border-b-0 hover:bg-bg-hover transition-colors">
                  <td className="px-4 py-3 text-xs text-text-secondary font-mono">
                    {new Date(log.timestamp).toLocaleString('ru-RU')}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono font-semibold">
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-text-secondary">
                    <div>{log.resource_type}</div>
                    {log.resource_name && (
                      <div className="font-mono text-text-muted">{log.resource_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-text-secondary">
                    {log.dc_code ? (
                      <span className="px-2 py-0.5 bg-bg-input rounded-sm font-mono border border-border">
                        {log.dc_code}
                      </span>
                    ) : (
                      <span className="text-text-muted">Global</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-text-secondary">
                    {log.user_role || 'System'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-sm text-xs font-medium ${severityColor(
                        log.severity
                      )}`}
                    >
                      {log.severity}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-3 border-t border-border flex items-center justify-between">
              <span className="text-xs text-text-secondary">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-bg-input border border-border rounded-sm text-xs text-text-secondary hover:bg-bg-hover disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 bg-bg-input border border-border rounded-sm text-xs text-text-secondary hover:bg-bg-hover disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
